import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, Easing } from "remotion";
import { COLORS, FONT, NEUTRAL, SAFE } from "../theme";
import { Kicker } from "../components";

/* Mapa animado de Ecuador: silueta del país + ciudades reales y la ruta del
   vehículo dibujándose de la costa (Guayaquil) a la sierra (Quito). */

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

// Proyección simple lng/lat -> x/y (equirectangular, válida cerca del ecuador).
const S = 110;
const P = (lng: number, lat: number): [number, number] => [(lng + 81) * S, (1.6 - lat) * S];

// Frontera aproximada de Ecuador continental (lng, lat), en sentido horario.
const BORDER: [number, number][] = [
  [-78.9, 1.45], [-77.9, 0.9], [-77.2, 0.6], [-76.4, 0.45], [-75.8, 0.1],
  [-75.5, -0.6], [-75.9, -1.5], [-76.6, -2.6], [-77.8, -3.0], [-78.4, -3.5],
  [-78.7, -4.2], [-79.2, -4.7], [-79.7, -4.4], [-80.0, -3.9], [-79.9, -3.4],
  [-80.2, -2.9], [-80.9, -2.3], [-81.0, -1.9], [-80.8, -1.05], [-80.55, -0.95],
  [-80.45, -0.55], [-80.1, -0.15], [-80.05, 0.35], [-79.65, 0.82], [-79.6, 1.05],
];

type City = { name: string; lng: number; lat: number; anchor: "start" | "end"; dx: number; dy: number };
const CITIES: City[] = [
  { name: "Quito", lng: -78.47, lat: -0.18, anchor: "start", dx: 16, dy: 6 },
  { name: "Santo Domingo", lng: -79.17, lat: -0.25, anchor: "end", dx: -16, dy: 4 },
  { name: "Manta", lng: -80.73, lat: -0.95, anchor: "start", dx: 16, dy: 6 },
  { name: "Ambato", lng: -78.62, lat: -1.24, anchor: "start", dx: 16, dy: 6 },
  { name: "Guayaquil", lng: -79.92, lat: -2.17, anchor: "end", dx: -16, dy: 6 },
  { name: "Cuenca", lng: -79.0, lat: -2.9, anchor: "start", dx: 16, dy: 6 },
  { name: "Machala", lng: -79.96, lat: -3.26, anchor: "end", dx: -16, dy: 6 },
  { name: "Loja", lng: -79.2, lat: -4.0, anchor: "start", dx: 16, dy: 6 },
];

// Ruta del vehículo: Guayaquil -> Cuenca -> Ambato -> Quito.
const ROUTE_NAMES = ["Guayaquil", "Cuenca", "Ambato", "Quito"];
const ROUTE = ROUTE_NAMES.map((n) => {
  const c = CITIES.find((x) => x.name === n)!;
  return P(c.lng, c.lat);
});

// Longitudes acumuladas de la polilínea de ruta.
const segLen: number[] = [];
let totalLen = 0;
for (let i = 1; i < ROUTE.length; i++) {
  const d = Math.hypot(ROUTE[i][0] - ROUTE[i - 1][0], ROUTE[i][1] - ROUTE[i - 1][1]);
  segLen.push(d);
  totalLen += d;
}
const posAt = (p: number): [number, number] => {
  let d = p * totalLen;
  for (let i = 0; i < segLen.length; i++) {
    if (d <= segLen[i]) {
      const t = segLen[i] === 0 ? 0 : d / segLen[i];
      return [
        ROUTE[i][0] + (ROUTE[i + 1][0] - ROUTE[i][0]) * t,
        ROUTE[i][1] + (ROUTE[i + 1][1] - ROUTE[i][1]) * t,
      ];
    }
    d -= segLen[i];
  }
  return ROUTE[ROUTE.length - 1];
};
// Fracción acumulada de cada ciudad de la ruta (para encenderlas al llegar).
const routeFrac = ROUTE.map((_, i) => {
  let acc = 0;
  for (let k = 0; k < i; k++) acc += segLen[k];
  return acc / totalLen;
});

// bbox para el viewBox.
const allPts = [...BORDER.map(([a, b]) => P(a, b)), ...CITIES.map((c) => P(c.lng, c.lat))];
const xs = allPts.map((p) => p[0]);
const ys = allPts.map((p) => p[1]);
const minX = Math.min(...xs) - 30, minY = Math.min(...ys) - 30;
const vbW = Math.max(...xs) - minX + 30, vbH = Math.max(...ys) - minY + 30;

const borderPath =
  BORDER.map((c, i) => `${i === 0 ? "M" : "L"} ${P(c[0], c[1])[0].toFixed(1)} ${P(c[0], c[1])[1].toFixed(1)}`).join(" ") + " Z";
const routePath = ROUTE.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");

export const SMapa: React.FC = () => {
  const frame = useCurrentFrame();

  const shapeIn = interpolate(frame, [0, 24], [0, 1], { ...clamp, easing: Easing.bezier(0.16, 1, 0.3, 1) });
  const progress = interpolate(frame, [30, 165], [0, 1], { ...clamp, easing: Easing.bezier(0.4, 0, 0.3, 1) });
  const [mx, my] = posAt(progress);
  const equatorY = P(0, 0)[1];

  const titleOp = interpolate(frame, [6, 26], [0, 1], clamp);
  const titleY = interpolate(frame, [6, 26], [24, 0], { ...clamp, easing: Easing.bezier(0.16, 1, 0.3, 1) });

  return (
    <AbsoluteFill style={{ backgroundColor: COLORS.bg, overflow: "hidden" }}>
      <AbsoluteFill
        style={{
          background:
            "radial-gradient(1100px 640px at 75% 30%, rgba(95,160,228,0.16), transparent 60%), radial-gradient(900px 620px at 60% 90%, rgba(124,92,191,0.14), transparent 60%)",
        }}
      />

      {/* título a la izquierda */}
      <div style={{ position: "absolute", left: SAFE, top: 300, opacity: titleOp, translate: `0px ${titleY}px`, maxWidth: 560, zIndex: 2 }}>
        <div style={{ marginBottom: 14 }}>
          <Kicker>En ruta · Ecuador</Kicker>
        </div>
        <div style={{ fontFamily: FONT, fontWeight: 800, fontSize: 74, color: COLORS.white, letterSpacing: -1.5, lineHeight: 1.05, textWrap: "balance" }}>
          Sigue el recorrido, de la costa a la sierra
        </div>
        <div style={{ marginTop: 26, display: "flex", alignItems: "center", gap: 12, fontFamily: FONT, fontSize: 30, color: COLORS.sub }}>
          <span style={{ width: 26, height: 5, borderRadius: 5, background: `linear-gradient(90deg, ${COLORS.blue}, ${COLORS.purple})` }} />
          Ubicación en tiempo real
        </div>
      </div>

      {/* mapa a la derecha */}
      <div style={{ position: "absolute", right: 90, top: 60, height: 960, opacity: shapeIn, scale: String(0.94 + shapeIn * 0.06), transformOrigin: "50% 50%" }}>
        <svg height="960" viewBox={`${minX} ${minY} ${vbW} ${vbH}`} fill="none">
          {/* silueta país */}
          <path d={borderPath} fill="#132436" stroke={NEUTRAL.lineStrong} strokeWidth="2.5" />
          <path d={borderPath} fill="url(#mapShade)" />

          {/* línea del ecuador */}
          <line x1={minX + 6} y1={equatorY} x2={minX + vbW - 6} y2={equatorY} stroke={COLORS.amber} strokeWidth="1.6" strokeDasharray="7 8" opacity="0.55" />
          <text x={minX + vbW - 12} y={equatorY - 8} fill={COLORS.amber} opacity="0.8" fontFamily={FONT} fontSize="15" textAnchor="end">
            Latitud 0°
          </text>

          {/* ruta base (tenue) y ruta dibujada */}
          <path d={routePath} stroke={NEUTRAL.line} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <path
            d={routePath}
            stroke="url(#routeGrad)"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={totalLen}
            strokeDashoffset={totalLen * (1 - progress)}
          />

          {/* ciudades */}
          {CITIES.map((c) => {
            const [x, y] = P(c.lng, c.lat);
            const ri = ROUTE_NAMES.indexOf(c.name);
            const reached = ri >= 0 && progress >= routeFrac[ri] - 0.001;
            const onRoute = ri >= 0;
            const col = reached ? COLORS.greenLight : onRoute ? COLORS.blueLight : COLORS.sub;
            return (
              <g key={c.name}>
                {reached && <circle cx={x} cy={y} r="14" fill={COLORS.greenLight} opacity="0.18" />}
                <circle cx={x} cy={y} r={onRoute ? 7 : 5} fill={col} stroke="#0A1220" strokeWidth="2" />
                <text
                  x={x + c.dx}
                  y={y + c.dy}
                  fill={reached ? COLORS.white : COLORS.sub}
                  fontFamily={FONT}
                  fontSize="19"
                  fontWeight={onRoute ? 700 : 500}
                  textAnchor={c.anchor}
                >
                  {c.name}
                </text>
              </g>
            );
          })}

          {/* marcador del vehículo en movimiento */}
          <g>
            <circle cx={mx} cy={my} r="16" fill={COLORS.white} opacity="0.14" />
            <circle cx={mx} cy={my} r="9" fill={COLORS.white} stroke={COLORS.blue} strokeWidth="3" />
          </g>

          <defs>
            <linearGradient id="routeGrad" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0" stopColor={COLORS.blue} />
              <stop offset="1" stopColor={COLORS.purple} />
            </linearGradient>
            <linearGradient id="mapShade" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#ffffff" stopOpacity="0.05" />
              <stop offset="1" stopColor="#000000" stopOpacity="0.15" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </AbsoluteFill>
  );
};
